import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { BookOpen } from 'lucide-react';
import type { RatingDailyRecord, RatingDateBase } from '../../types/styling-analysis.js';
import type { Granularity } from '../../lib/kpi-aggregation.js';
import { GRANULARITY_OPTIONS } from '../../lib/kpi-aggregation.js';
import { RATING_DEFINITIONS } from '../../lib/rating-definitions.js';
import { aggregateRatingData, type SegmentFilter } from '../../lib/rating-aggregation.js';
import {
  TOOLTIP_CONTENT_STYLE,
  AXIS_TICK_STYLE,
  GRID_PROPS,
  LEGEND_CLICKABLE_PROPS,
} from '../../lib/chart-theme.js';
import { useSeriesToggle } from '../../hooks/useSeriesToggle.js';
import { ChartFilterBar } from '../chart/ChartFilterBar.js';

/** @graph-connects none */
const CHART_COLORS = {
  totalOrders: '#0017C1',
  respondedOrders: '#7096F8',
  avgRating: '#FE3939',
  avgDesign: '#FF8C00',
  avgColor: '#22C55E',
  goodRate: '#FE3939',
} as const;

/** @graph-connects none */
interface RatingAnalysisPanelProps {
  daily: RatingDailyRecord[];
  dateBase: RatingDateBase;
  onDateBaseChange: (dateBase: RatingDateBase) => void;
}

/**
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business レーティング分析パネル。2つの ComposedChart（レーティング推移 + スタイリスト評価推移）を粒度切替・起算日切替・セグメントフィルタ・定義表示付きで表示する。
 * @graph-connects aggregateRatingData [calls] 日次データを粒度・セグメントで集約
 * @graph-connects RATING_DEFINITIONS [references] 指標定義データ
 */
export function RatingAnalysisPanel({
  daily,
  dateBase,
  onDateBaseChange,
}: RatingAnalysisPanelProps) {
  const { t } = useTranslation();
  const [granularity, setGranularity] = useState<Granularity>('monthly');
  const [segment, setSegment] = useState<SegmentFilter>('all');
  const { hiddenSeries, handleLegendClick } = useSeriesToggle();
  const [showDefinitions, setShowDefinitions] = useState(false);

  const data = useMemo(
    () => aggregateRatingData(daily, granularity, segment),
    [daily, granularity, segment],
  );

  const summary = useMemo(() => {
    if (data.length === 0)
      return {
        totalOrders: 0,
        avgRating: null as number | null,
        avgDesign: null as number | null,
        avgColor: null as number | null,
        goodRate: null as number | null,
        ratingNullRate: 0,
        reviewNullRate: 0,
      };
    const totalOrders = data.reduce((s, d) => s + d.totalOrders, 0);
    const ratingResponded = data.reduce((s, d) => s + d.ratingRespondedOrders, 0);
    const reviewResponded = data.reduce((s, d) => s + d.stylingReviewRespondedOrders, 0);
    const goodTotal = data.reduce((s, d) => s + d.goodCount, 0);
    const badTotal = data.reduce((s, d) => s + d.badCount, 0);
    const designSum = data.reduce(
      (s, d) => s + (d.avgDesignRating != null ? d.avgDesignRating * d.ratingRespondedOrders : 0),
      0,
    );
    const colorSum = data.reduce(
      (s, d) => s + (d.avgColorRating != null ? d.avgColorRating * d.ratingRespondedOrders : 0),
      0,
    );
    const avgDesign =
      ratingResponded > 0 ? Math.round((designSum / ratingResponded) * 100) / 100 : null;
    const avgColor =
      ratingResponded > 0 ? Math.round((colorSum / ratingResponded) * 100) / 100 : null;
    const reviewed = goodTotal + badTotal;
    return {
      totalOrders,
      avgRating:
        avgDesign != null && avgColor != null
          ? Math.round(((avgDesign + avgColor) / 2) * 100) / 100
          : null,
      avgDesign,
      avgColor,
      goodRate: reviewed > 0 ? Math.round((goodTotal / reviewed) * 1000) / 10 : null,
      ratingNullRate:
        totalOrders > 0
          ? Math.round(((totalOrders - ratingResponded) / totalOrders) * 1000) / 10
          : 0,
      reviewNullRate:
        totalOrders > 0
          ? Math.round(((totalOrders - reviewResponded) / totalOrders) * 1000) / 10
          : 0,
    };
  }, [data]);

  return (
    <div className="space-y-6 p-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4">
        <ChartFilterBar
          label={t('styling.dateBase')}
          options={[
            { key: 'completed' as const, label: t('styling.dateBaseCompleted') },
            { key: 'received' as const, label: t('styling.dateBaseReceived') },
          ]}
          value={dateBase}
          onChange={onDateBaseChange}
        />
        <ChartFilterBar
          label={t('styling.segment')}
          options={[
            { key: 'all' as const, label: t('styling.segmentAll') },
            { key: 'first' as const, label: t('styling.segmentFirst') },
            { key: 'repeat' as const, label: t('styling.segmentRepeat') },
          ]}
          value={segment}
          onChange={setSegment}
        />
        <ChartFilterBar
          label={t('styling.granularity')}
          options={GRANULARITY_OPTIONS}
          value={granularity}
          onChange={setGranularity}
        />
        <button
          type="button"
          onClick={() => {
            setShowDefinitions((p) => !p);
          }}
          className={`ml-auto flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs transition-colors ${
            showDefinitions
              ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)]/10 text-[var(--color-accent-primary)]'
              : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]'
          }`}
        >
          <BookOpen size={12} />
          {t('styling.definitions')}
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card className="p-3">
          <div className="text-xs text-[var(--color-text-muted)]">{t('styling.avgRating')}</div>
          <div className="mt-1 text-xl font-bold tabular-nums text-[var(--color-text-primary)]">
            {summary.avgRating != null ? summary.avgRating.toFixed(2) : '—'}
            <span className="ml-0.5 text-sm font-normal text-[var(--color-text-muted)]">/ 3.0</span>
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-[var(--color-text-muted)]">{t('styling.goodRate')}</div>
          <div className="mt-1 text-xl font-bold tabular-nums text-[var(--color-text-primary)]">
            {summary.goodRate != null ? `${String(summary.goodRate)}%` : '—'}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-[var(--color-text-muted)]">{t('styling.orderCount')}</div>
          <div className="mt-1 text-xl font-bold tabular-nums text-[var(--color-text-primary)]">
            {summary.totalOrders.toLocaleString('ja-JP')}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-[var(--color-text-muted)]">
            {t('styling.noResponseRate')}
          </div>
          <div className="mt-0.5 text-sm tabular-nums text-[var(--color-text-secondary)]">
            {t('styling.ratingNoResponse', { value: String(summary.ratingNullRate) })}
          </div>
          <div className="text-sm tabular-nums text-[var(--color-text-secondary)]">
            Good/Bad: {String(summary.reviewNullRate)}%
          </div>
        </Card>
      </div>

      {/* Rating Chart */}
      {data.length > 0 && (
        <>
          <div>
            <h4 className="mb-2 text-sm font-medium text-[var(--color-text-secondary)]">
              {t('styling.ratingTrend')}
            </h4>
            <Card className="p-4">
              <div style={{ width: '100%', height: 340 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                    <CartesianGrid {...GRID_PROPS} />
                    <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
                    <YAxis
                      yAxisId="left"
                      tick={AXIS_TICK_STYLE}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v: number) => v.toLocaleString('ja-JP')}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      domain={[0, 3]}
                      tick={AXIS_TICK_STYLE}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} />
                    <Legend {...LEGEND_CLICKABLE_PROPS} onClick={handleLegendClick} />
                    <Bar
                      yAxisId="left"
                      dataKey="totalOrders"
                      name={t('styling.orderCount')}
                      fill={CHART_COLORS.totalOrders}
                      opacity={0.75}
                      radius={[2, 2, 0, 0]}
                      hide={hiddenSeries.has('totalOrders')}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="ratingRespondedOrders"
                      name={t('styling.responseCount')}
                      fill={CHART_COLORS.respondedOrders}
                      opacity={0.75}
                      radius={[2, 2, 0, 0]}
                      hide={hiddenSeries.has('ratingRespondedOrders')}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="avgRating"
                      name={t('styling.avgRating')}
                      stroke={CHART_COLORS.avgRating}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      connectNulls
                      hide={hiddenSeries.has('avgRating')}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="avgDesignRating"
                      name={t('styling.avgDesignRating')}
                      stroke={CHART_COLORS.avgDesign}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      connectNulls
                      hide={hiddenSeries.has('avgDesignRating')}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="avgColorRating"
                      name={t('styling.avgColorRating')}
                      stroke={CHART_COLORS.avgColor}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      connectNulls
                      hide={hiddenSeries.has('avgColorRating')}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* Stylist Good Rate Chart */}
          <div>
            <h4 className="mb-2 text-sm font-medium text-[var(--color-text-secondary)]">
              {t('styling.stylistRatingTrend')}
            </h4>
            <Card className="p-4">
              <div style={{ width: '100%', height: 340 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                    <CartesianGrid {...GRID_PROPS} />
                    <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
                    <YAxis
                      yAxisId="left"
                      tick={AXIS_TICK_STYLE}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v: number) => v.toLocaleString('ja-JP')}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      domain={[0, 100]}
                      tick={AXIS_TICK_STYLE}
                      tickLine={false}
                      axisLine={false}
                      unit="%"
                    />
                    <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} />
                    <Legend {...LEGEND_CLICKABLE_PROPS} onClick={handleLegendClick} />
                    <Bar
                      yAxisId="left"
                      dataKey="totalOrders"
                      name={t('styling.orderCount')}
                      fill={CHART_COLORS.totalOrders}
                      opacity={0.75}
                      radius={[2, 2, 0, 0]}
                      hide={hiddenSeries.has('totalOrders')}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="stylingReviewRespondedOrders"
                      name={t('styling.responseCount')}
                      fill={CHART_COLORS.respondedOrders}
                      opacity={0.75}
                      radius={[2, 2, 0, 0]}
                      hide={hiddenSeries.has('stylingReviewRespondedOrders')}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="goodRate"
                      name={t('styling.goodRate')}
                      stroke={CHART_COLORS.goodRate}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      connectNulls
                      hide={hiddenSeries.has('goodRate')}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </>
      )}

      {data.length === 0 && (
        <div className="px-6 py-12 text-center text-sm text-[var(--color-text-muted)]">
          {t('styling.noDataPeriod')}
        </div>
      )}

      {/* Definitions */}
      {showDefinitions && (
        <div>
          <h4 className="mb-3 text-sm font-medium text-[var(--color-text-secondary)]">
            {t('styling.metricDefinitions')}
          </h4>
          <div className="grid gap-3 md:grid-cols-2">
            {Object.entries(RATING_DEFINITIONS).map(([key, def]) => (
              <Card key={key} className="p-4">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-[var(--color-text-primary)]">{def.label}</span>
                  <code className="ml-auto rounded bg-[var(--color-bg-subtle)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)]">
                    {key}
                  </code>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-[var(--color-text-secondary)]">
                  {def.businessDescription}
                </p>
                <dl className="mt-2 border-t border-[var(--color-border)] pt-2">
                  <dt className="text-[10px] font-medium text-[var(--color-text-muted)]">
                    {t('styling.sourceDb')}
                  </dt>
                  <dd className="font-mono text-[10px] text-[var(--color-text-secondary)]">
                    {def.technicalSource.database}
                  </dd>
                  <dt className="mt-1 text-[10px] font-medium text-[var(--color-text-muted)]">
                    {t('styling.table')}
                  </dt>
                  <dd className="font-mono text-[10px] text-[var(--color-text-secondary)]">
                    {def.technicalSource.tables.join(', ')}
                  </dd>
                  <dt className="mt-1 text-[10px] font-medium text-[var(--color-text-muted)]">
                    {t('styling.logic')}
                  </dt>
                  <dd className="text-[10px] leading-relaxed text-[var(--color-text-secondary)]">
                    <pre className="whitespace-pre-wrap font-mono">{def.technicalSource.logic}</pre>
                  </dd>
                </dl>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
